import React, { useLayoutEffect, useRef } from 'react'
import { Form } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { ReactComponent as ZocksLogo } from '../../../assets/ZocksLogo.svg'
import { MAX_USER_QUESTION_LENGTH } from './hooks'
import { useAnimatedHeight } from './useAnimatedHeight'

export type CrossClientQueryInputMode = 'input' | 'working' | 'review'

interface CrossClientQueryInputProps {
  userQuestion: string
  onQuestionChange: (question: string) => void
  onSubmit: () => Promise<void>
  disabled: boolean
  submitDisabled: boolean
  isLoading: boolean
  hasExecution: boolean
  mode?: CrossClientQueryInputMode
  reviewContent?: React.ReactNode
  footerLeft?: React.ReactNode
  footerRight?: React.ReactNode
}

const fitTextareaToContent = (textarea: HTMLTextAreaElement) => {
  textarea.style.height = 'auto'
  textarea.style.height = `${textarea.scrollHeight}px`
}

export const CrossClientQueryInput = ({
  userQuestion,
  onQuestionChange,
  onSubmit,
  disabled,
  submitDisabled,
  isLoading,
  hasExecution,
  mode = 'input',
  reviewContent,
  footerLeft,
  footerRight,
}: CrossClientQueryInputProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useLayoutEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) {
      return
    }
    fitTextareaToContent(textarea)
  }, [userQuestion, mode])

  // A width change re-wraps the text, so refit then. Height-only notifications come from the refit itself; ignore them.
  useLayoutEffect(() => {
    const textarea = textareaRef.current
    if (!textarea || typeof ResizeObserver === 'undefined') {
      return
    }
    let lastWidth = textarea.offsetWidth
    const observer = new ResizeObserver(() => {
      if (textarea.offsetWidth === lastWidth) {
        return
      }
      lastWidth = textarea.offsetWidth
      fitTextareaToContent(textarea)
    })
    observer.observe(textarea)
    return () => observer.disconnect()
  }, [mode])

  const contentRef = useRef<HTMLDivElement>(null)
  useAnimatedHeight(contentRef, mode)

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isLoading || hasExecution) {
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      if (e.ctrlKey || e.metaKey) {
        onQuestionChange(`${userQuestion}\n`)
      } else if (!submitDisabled) {
        await onSubmit()
      }
    }
  }

  return (
    <div className='d-flex flex-column'>
      <div ref={contentRef} className='px-3 pt-3 pb-3'>
        {mode === 'input' ? (
          <Form.Control
            className='p-0 border-0 cross-client-input-textarea'
            as='textarea'
            ref={textareaRef}
            rows={1}
            value={userQuestion}
            maxLength={MAX_USER_QUESTION_LENGTH}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              onQuestionChange(e.currentTarget.value)
            }}
            disabled={disabled}
            onKeyDown={handleKeyDown}
            placeholder='Example: Which clients are approaching retirement with over $500k in assets?'
          />
        ) : (
          <div className='d-flex flex-column gap-3 cross-client-working'>
            <p className='paragraph-medium cross-client-working__question'>{userQuestion}</p>
            {mode === 'working' ? (
              <div className='d-flex align-items-center zocks-gap-0.75 cross-client-working__status'>
                <div className='d-flex align-items-center justify-content-center bg-black ai-agent-chat-message-logo zocks-border-0.25'>
                  <ZocksLogo width={12} height={12} />
                </div>
                <span className='paragraph-medium shimmering-loader-text'>Working out what to look for</span>
              </div>
            ) : (
              reviewContent
            )}
          </div>
        )}
      </div>
      <div className='w-100 cross-client-chat-divider' />
      <div className='d-flex align-items-center justify-content-between px-3 py-2 gap-3'>
        <div className='d-flex gap-2 align-items-center'>{footerLeft}</div>
        <div className='d-flex gap-2 align-items-center cross-client-chat-footer-actions'>
          {footerRight}
          {mode === 'input' ? (
            <Button
              className='d-inline-flex align-items-center justify-content-center cross-client-send-button'
              onClick={onSubmit}
              disabled={submitDisabled || isLoading || hasExecution}
              aria-label='Submit query'
            >
              <i className='bi bi-arrow-up' />
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
